import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Plus, Pencil, Trash2, QrCode, Download, BarChart3, Eye, EyeOff, X, Upload,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { SECTIONS } from "@/config";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const TYPE_LABELS = {
  accommodation: "Alloggi", restaurant: "Ristoranti", activity: "Esperienze", sport: "Sport",
  winery: "Cantine", product: "Prodotti tipici", dish: "Piatti tipici", recipe: "Ricette",
  event: "Eventi", offer: "Pacchetti", info: "Info utili",
};

const emptyItem = (type) => ({
  slug: "", type, category: "", zone: "",
  title: { it: "", en: "", de: "" }, excerpt: { it: "", en: "", de: "" }, description: { it: "", en: "", de: "" },
  images: [], address: "", lat: "", lng: "", phone: "", email: "", website: "", ticket_link: "",
  price_range: "", tags: [], family: false, pets: false, published: true, sponsored: false,
  start: "", end: "", links_dishes: [], links_items: [],
});

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-grafite/50 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full rounded-xl border border-black/10 bg-calce px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-turchese";

function ItemForm({ type, item, onClose, onSaved }) {
  const isEdit = !!item;
  const [f, setF] = useState(() => {
    const base = item ? { ...emptyItem(type), ...item } : emptyItem(type);
    return {
      ...base,
      imagesText: (base.images || []).join("\n"),
      tagsText: (base.tags || []).join(", "),
      linksDishesText: (base.links_dishes || []).join(", "),
      linksItemsText: (base.links_items || []).join(", "),
      start: base.start ? String(base.start).slice(0, 16) : "",
      end: base.end ? String(base.end).slice(0, 16) : "",
      lat: base.lat ?? "", lng: base.lng ?? "",
      profilesText: (base.profiles || []).join(", "),
    };
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setF((p) => ({ ...p, imagesText: (p.imagesText ? p.imagesText + "\n" : "") + data.url }));
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setI18n = (k, lang, v) => setF((p) => ({ ...p, [k]: { ...p[k], [lang]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...f,
      images: f.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      tags: f.tagsText.split(",").map((s) => s.trim()).filter(Boolean),
      links_dishes: f.linksDishesText.split(",").map((s) => s.trim()).filter(Boolean),
      links_items: f.linksItemsText.split(",").map((s) => s.trim()).filter(Boolean),
      profiles: f.profilesText.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean),
      lat: f.lat === "" ? null : parseFloat(f.lat),
      lng: f.lng === "" ? null : parseFloat(f.lng),
      start: f.start || null,
      end: f.end || null,
    };
    delete payload.imagesText; delete payload.tagsText; delete payload.linksDishesText; delete payload.linksItemsText; delete payload.profilesText;
    try {
      if (isEdit) await api.put(`/admin/items/${item.slug}`, payload);
      else await api.post("/admin/items", payload);
      toast.success(isEdit ? "Scheda aggiornata" : "Scheda creata");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-testid="item-form">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Slug (univoco, no spazi)">
          <input required disabled={isEdit} value={f.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} className={inputCls} data-testid="form-slug" />
        </Field>
        <Field label="Categoria"><input value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="form-category" /></Field>
      </div>
      {["it", "en", "de", "fr"].map((lng) => (
        <div key={lng} className="rounded-2xl bg-sabbia/40 p-4 flex flex-col gap-3">
          <p className="text-xs font-bold uppercase tracking-wider text-mare">{lng.toUpperCase()}</p>
          <Field label="Titolo"><input required={lng === "it"} value={f.title[lng]} onChange={(e) => setI18n("title", lng, e.target.value)} className={inputCls} data-testid={`form-title-${lng}`} /></Field>
          <Field label="Descrizione breve"><input value={f.excerpt[lng]} onChange={(e) => setI18n("excerpt", lng, e.target.value)} className={inputCls} /></Field>
          <Field label="Descrizione lunga"><textarea rows={3} value={f.description[lng]} onChange={(e) => setI18n("description", lng, e.target.value)} className={inputCls} /></Field>
        </div>
      ))}
      <Field label="Immagini (un URL per riga)">
        <textarea rows={3} value={f.imagesText} onChange={(e) => set("imagesText", e.target.value)} className={inputCls} data-testid="form-images" />
      </Field>
      <label className={`inline-flex items-center gap-2 rounded-full bg-mare/10 text-mare text-xs font-bold px-4 py-2.5 cursor-pointer hover:bg-mare/20 transition-colors self-start ${uploading ? "opacity-60" : ""}`}>
        <Upload className="w-3.5 h-3.5" /> {uploading ? "Carico…" : "Carica foto dal tuo computer"}
        <input type="file" accept="image/*" className="hidden" data-testid="form-upload" onChange={uploadFile} disabled={uploading} />
      </label>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Indirizzo"><input value={f.address} onChange={(e) => set("address", e.target.value)} className={inputCls} /></Field>
        <Field label="Zona"><input value={f.zone} onChange={(e) => set("zone", e.target.value)} className={inputCls} /></Field>
        <Field label="Latitudine"><input value={f.lat} onChange={(e) => set("lat", e.target.value)} className={inputCls} placeholder="42.91" /></Field>
        <Field label="Longitudine"><input value={f.lng} onChange={(e) => set("lng", e.target.value)} className={inputCls} placeholder="11.18" /></Field>
        <Field label="Telefono"><input value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></Field>
        <Field label="Email"><input value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Field>
        <Field label="Sito web"><input value={f.website} onChange={(e) => set("website", e.target.value)} className={inputCls} /></Field>
        <Field label="Fascia prezzo">
          <select value={f.price_range} onChange={(e) => set("price_range", e.target.value)} className={inputCls}>
            <option value="">—</option><option value="€">€</option><option value="€€">€€</option><option value="€€€">€€€</option>
          </select>
        </Field>
      </div>
      {type === "event" && (
        <div className="grid grid-cols-2 gap-4 rounded-2xl bg-turchese/10 p-4">
          <Field label="Inizio"><input type="datetime-local" value={f.start} onChange={(e) => set("start", e.target.value)} className={inputCls} data-testid="form-start" /></Field>
          <Field label="Fine"><input type="datetime-local" value={f.end} onChange={(e) => set("end", e.target.value)} className={inputCls} /></Field>
          <Field label="Link biglietti"><input value={f.ticket_link} onChange={(e) => set("ticket_link", e.target.value)} className={inputCls} /></Field>
        </div>
      )}
      <Field label="Tag (separati da virgola)"><input value={f.tagsText} onChange={(e) => set("tagsText", e.target.value)} className={inputCls} /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Piatti collegati (slug, virgola)"><input value={f.linksDishesText} onChange={(e) => set("linksDishesText", e.target.value)} className={inputCls} /></Field>
        <Field label="Schede collegate (slug, virgola)"><input value={f.linksItemsText} onChange={(e) => set("linksItemsText", e.target.value)} className={inputCls} /></Field>
      </div>
      {type === "offer" && (
        <div className="rounded-2xl bg-terracotta/10 p-4">
          <Field label="Profili target (virgola): famiglie, coppie, wine, sport, eventi, amici">
            <input value={f.profilesText} onChange={(e) => set("profilesText", e.target.value)} className={inputCls} data-testid="form-profiles" placeholder="famiglie, coppie" />
          </Field>
        </div>
      )}
      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={f.family} onCheckedChange={(v) => set("family", v)} data-testid="form-family" /> Famiglie</label>
        <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={f.pets} onCheckedChange={(v) => set("pets", v)} /> Animali</label>
        <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={f.published} onCheckedChange={(v) => set("published", v)} data-testid="form-published" /> Pubblicato</label>
        <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={!!f.sponsored} onCheckedChange={(v) => set("sponsored", v)} data-testid="form-sponsored" /> In evidenza</label>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} data-testid="form-save"
          className="rounded-full bg-terracotta hover:bg-[#d15f2d] disabled:opacity-60 text-white font-bold px-6 py-3 text-sm transition-colors">
          {saving ? "Salvo…" : isEdit ? "Salva modifiche" : "Crea scheda"}
        </button>
        <button type="button" onClick={onClose} className="rounded-full border border-black/10 px-6 py-3 text-sm font-semibold hover:bg-sabbia transition-colors">Annulla</button>
      </div>
    </form>
  );
}

function QrBox({ url, label, size = 160 }) {
  const download = () => {
    const canvas = document.getElementById(`qr-${label}`);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-${label}.png`;
    a.click();
  };
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm flex flex-col items-center gap-3" data-testid={`qr-box-${label}`}>
      <QRCodeCanvas id={`qr-${label}`} value={url} size={size} includeMargin />
      <p className="text-xs text-grafite/60 break-all text-center max-w-[200px]">{url}</p>
      <button onClick={download} data-testid={`qr-download-${label}`}
        className="inline-flex items-center gap-2 rounded-full bg-mare hover:bg-[#0b5a73] text-white text-xs font-bold px-4 py-2 transition-colors">
        <Download className="w-3.5 h-3.5" /> Scarica PNG
      </button>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [surveyStats, setSurveyStats] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | item
  const [newQ, setNewQ] = useState({ it: "", en: "", de: "", fr: "", kind: "rating", optionsText: "" });
  const [savingQ, setSavingQ] = useState(false);
  const origin = "https://likefollonica.it";

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const loadItems = (type) => api.get("/admin/items", { params: { type } }).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  const loadSurvey = () => {
    api.get("/admin/survey/stats").then((r) => setSurveyStats(r.data)).catch(() => {});
    api.get("/admin/survey/questions").then((r) => setQuestions(r.data)).catch(() => {});
    api.get("/admin/survey/contacts").then((r) => setContacts(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!me) return;
    if (tab === "overview") loadStats();
    else if (tab === "survey") loadSurvey();
    else loadItems(tab);
  }, [tab, me]);

  const logout = async () => {
    localStorage.removeItem("fl-token");
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const del = async (slug) => {
    if (!window.confirm(`Eliminare "${slug}"?`)) return;
    try {
      await api.delete(`/admin/items/${slug}`);
      toast.success("Eliminata");
      loadItems(tab);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePub = async (slug, published) => {
    try {
      await api.patch(`/admin/items/${slug}/publish`, { published });
      setItems((prev) => prev.map((i) => (i.slug === slug ? { ...i, published } : i)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/survey/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sondaggio_follonica_like.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const tabs = [{ id: "overview", label: "Panoramica" }, ...SECTIONS.map((s) => ({ id: s.type, label: TYPE_LABELS[s.type] })), { id: "survey", label: "Sondaggio" }];

  if (!me) return <p className="p-10 text-center text-grafite/50">Carico…</p>;

  return (
    <div className="min-h-screen bg-calce" data-testid="admin-dashboard">
      <header className="bg-mare text-calce sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <p className="font-heading font-bold">Follonica Like · Back-office</p>
          <div className="flex items-center gap-3">
            <span className="text-xs text-calce/70 hidden sm:block">{me.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-terracotta px-3.5 py-1.5 text-xs font-bold transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="admin-tabs">
          {tabs.map((tb) => (
            <button key={tb.id} data-testid={`tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`rounded-full px-4 py-2 text-xs font-bold whitespace-nowrap transition-colors ${tab === tb.id ? "bg-mare text-white" : "bg-white border border-black/10 text-grafite hover:bg-sabbia"}`}>
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="mt-6 grid gap-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="stats-cards">
              {SECTIONS.map((s) => (
                <div key={s.type} className="bg-white rounded-2xl p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wider text-grafite/40">{TYPE_LABELS[s.type]}</p>
                  <p className="font-heading font-bold text-3xl mt-1" style={{ color: s.color }}>{stats.items[s.type] || 0}</p>
                </div>
              ))}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wider text-grafite/40">Risposte sondaggio</p>
                <p className="font-heading font-bold text-3xl mt-1 text-pineta">{stats.survey_responses}</p>
              </div>
            </div>

            <section>
              <h2 className="font-heading font-bold text-xl mb-4 inline-flex items-center gap-2"><QrCode className="w-5 h-5 text-terracotta" /> QR code per la rivista</h2>
              <div className="flex flex-wrap gap-4">
                <QrBox label="eventi" url={`${origin}/eventi`} />
                <QrBox label="estate2026" url={`${origin}/eventi?fonte=estate2026`} />
                <QrBox label="inverno2026-27" url={`${origin}/eventi?fonte=inverno2026-27`} />
                <QrBox label="sondaggio" url={`${origin}/sondaggio?fonte=rivista`} />
              </div>
              {Object.keys(stats.qr_scans).length > 0 && (
                <div className="mt-4 bg-white rounded-2xl p-4 shadow-sm" data-testid="qr-scans">
                  <p className="text-xs font-bold uppercase tracking-wider text-grafite/40 mb-2">Scansioni per fonte</p>
                  {Object.entries(stats.qr_scans).map(([k, v]) => (
                    <p key={k} className="text-sm"><strong>{k}</strong>: {v} visite</p>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}

        {SECTIONS.some((s) => s.type === tab) && (
          <div className="mt-6" data-testid={`items-tab-${tab}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-xl">{TYPE_LABELS[tab]} <span className="text-grafite/40 text-base">({items.length})</span></h2>
              <button onClick={() => setEditing("new")} data-testid="item-new"
                className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-[#d15f2d] text-white text-sm font-bold px-5 py-2.5 transition-colors">
                <Plus className="w-4 h-4" /> Nuova scheda
              </button>
            </div>
            <div className="grid gap-3">
              {items.map((it) => (
                <div key={it.slug} data-testid={`row-${it.slug}`}
                  className={`bg-white rounded-2xl p-4 shadow-sm flex flex-wrap items-center gap-3 ${it.published ? "" : "opacity-55"}`}>
                  {it.images?.[0] && <img src={it.images[0]} alt="" className="w-14 h-14 rounded-xl object-cover" loading="lazy" />}
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-heading font-semibold flex items-center gap-2 flex-wrap">
                      {it.title?.it}
                      {it.pending && <span className="rounded-full bg-terracotta text-white text-[10px] font-bold px-2.5 py-0.5 uppercase tracking-wider" data-testid={`pending-${it.slug}`}>Da approvare</span>}
                    </p>
                    <p className="text-xs text-grafite/50">{it.slug} · {it.category} · ♥ {it.likes || 0}{it.proposer ? ` · proposta da ${it.proposer.name}` : ""}</p>
                  </div>
                  <button onClick={() => togglePub(it.slug, !it.published)} data-testid={`publish-${it.slug}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${it.published ? "bg-pineta/10 text-pineta" : it.pending ? "bg-terracotta text-white hover:bg-[#d15f2d]" : "bg-sabbia text-grafite/60"}`}>
                    {it.published ? <><Eye className="w-3.5 h-3.5" /> Online</> : it.pending ? <><Eye className="w-3.5 h-3.5" /> Approva</> : <><EyeOff className="w-3.5 h-3.5" /> Nascosta</>}
                  </button>
                  <button onClick={() => setEditing(it)} data-testid={`edit-${it.slug}`} className="w-9 h-9 rounded-full bg-sabbia hover:bg-turchese/30 flex items-center justify-center transition-colors" aria-label="Modifica">
                    <Pencil className="w-4 h-4 text-mare" />
                  </button>
                  <button onClick={() => del(it.slug)} data-testid={`delete-${it.slug}`} className="w-9 h-9 rounded-full bg-sabbia hover:bg-red-100 flex items-center justify-center transition-colors" aria-label="Elimina">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
            <section className="mt-8">
              <h3 className="font-heading font-bold text-lg mb-3 inline-flex items-center gap-2"><QrCode className="w-4 h-4 text-terracotta" /> QR singole schede</h3>
              <div className="flex flex-wrap gap-4">
                {items.filter((i) => i.published).slice(0, 12).map((it) => (
                  <QrBox key={it.slug} label={it.slug} url={`${origin}/scheda/${it.slug}`} size={110} />
                ))}
              </div>
            </section>
          </div>
        )}

        {tab === "survey" && surveyStats && (
          <div className="mt-6 grid gap-6" data-testid="survey-tab">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-heading font-bold text-xl inline-flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-pineta" /> Sondaggio — {surveyStats.total_responses} risposte
              </h2>
              <button onClick={downloadCsv} data-testid="survey-export"
                className="inline-flex items-center gap-2 rounded-full bg-mare hover:bg-[#0b5a73] text-white text-sm font-bold px-5 py-2.5 transition-colors">
                <Download className="w-4 h-4" /> Esporta CSV
              </button>
            </div>
            {surveyStats.questions.map((q) => (
              <div key={q.id} className="bg-white rounded-2xl p-5 shadow-sm" data-testid={`survey-stat-${q.id}`}>
                <p className="font-semibold">{q.question.it} <span className="text-xs text-grafite/40 font-normal">({q.total} risposte)</span></p>
                {q.kind === "rating" && (
                  <div className="mt-3">
                    <p className="font-heading font-bold text-2xl text-terracotta">{q.average} / 5</p>
                    <div className="mt-2 flex flex-col gap-1">
                      {Object.entries(q.distribution).map(([star, count]) => (
                        <div key={star} className="flex items-center gap-2 text-xs">
                          <span className="w-4 font-bold">{star}★</span>
                          <div className="h-2 rounded-full bg-turchese" style={{ width: `${q.total ? (count / q.total) * 100 : 0}%`, minWidth: count ? 4 : 0 }} />
                          <span className="text-grafite/50">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(q.kind === "multi" || q.kind === "profile") && (
                  <div className="mt-3 flex flex-col gap-1.5">
                    {Object.entries(q.counts || {}).sort((a, b) => b[1] - a[1]).map(([opt, count]) => (
                      <div key={opt} className="flex items-center gap-2 text-xs">
                        <span className="w-28 font-semibold truncate">{opt}</span>
                        <div className="h-2 rounded-full bg-pineta" style={{ width: `${q.total ? (count / q.total) * 100 : 0}%`, minWidth: 4 }} />
                        <span className="text-grafite/50">{count}</span>
                      </div>
                    ))}
                  </div>
                )}
                {q.kind === "text" && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(q.texts || []).slice(0, 40).map((txt, i) => (
                      <span key={i} className="rounded-full bg-sabbia px-3 py-1 text-xs">{txt}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="survey-contacts">
              <p className="font-semibold mb-1">Contatti raccolti (con consenso) — {contacts.length}</p>
              <p className="text-xs text-grafite/50 mb-3">Pronti da condividere con le strutture partner per le offerte in target.</p>
              {contacts.length === 0 ? (
                <p className="text-sm text-grafite/50">Nessun contatto ancora. Arrivano quando un turista lascia email o telefono nel sondaggio con consenso.</p>
              ) : (
                <div className="grid gap-1">
                  {contacts.map((c, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-3 py-2 border-b border-black/5 last:border-0 text-sm" data-testid={`contact-row-${i}`}>
                      <span className="font-semibold">{c.contact}</span>
                      {c.profile && <span className="rounded-full bg-terracotta/10 text-terracotta text-xs font-bold px-3 py-0.5">{c.profile}</span>}
                      {c.fonte && <span className="text-xs text-grafite/40">fonte: {c.fonte}</span>}
                      <span className="text-xs text-grafite/40 ml-auto">{c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <p className="font-semibold mb-3">Domande attive</p>
              {questions.map((q) => (
                <div key={q.id} className="flex items-center justify-between gap-3 py-2 border-b border-black/5 last:border-0">
                  <span className="text-sm flex-1">{q.question.it}</span>
                  <button onClick={async () => { await api.delete(`/admin/survey/questions/${q.id}`).catch(() => {}); loadSurvey(); }}
                    data-testid={`question-delete-${q.id}`}
                    className="w-8 h-8 rounded-full bg-sabbia hover:bg-red-100 flex items-center justify-center transition-colors shrink-0" aria-label="Elimina domanda">
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                  <Switch checked={q.active} data-testid={`question-toggle-${q.id}`}
                    onCheckedChange={async (v) => {
                      await api.patch(`/admin/survey/questions/${q.id}`, { published: v }).catch(() => {});
                      loadSurvey();
                    }} />
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="survey-question-form">
              <p className="font-semibold mb-3 inline-flex items-center gap-2"><Plus className="w-4 h-4 text-terracotta" /> Nuova domanda</p>
              <div className="grid gap-3">
                <input value={newQ.it} onChange={(e) => setNewQ((p) => ({ ...p, it: e.target.value }))}
                  placeholder="Testo della domanda (IT, obbligatorio)" className={inputCls} data-testid="newq-it" />
                <div className="grid sm:grid-cols-3 gap-3">
                  <input value={newQ.en} onChange={(e) => setNewQ((p) => ({ ...p, en: e.target.value }))} placeholder="EN (facoltativo)" className={inputCls} data-testid="newq-en" />
                  <input value={newQ.de} onChange={(e) => setNewQ((p) => ({ ...p, de: e.target.value }))} placeholder="DE (facoltativo)" className={inputCls} />
                  <input value={newQ.fr} onChange={(e) => setNewQ((p) => ({ ...p, fr: e.target.value }))} placeholder="FR (facoltativo)" className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <select value={newQ.kind} onChange={(e) => setNewQ((p) => ({ ...p, kind: e.target.value }))} className={inputCls} data-testid="newq-kind">
                    <option value="rating">Valutazione 1-5 stelle</option>
                    <option value="multi">Scelta multipla</option>
                    <option value="text">Testo libero</option>
                  </select>
                  {newQ.kind === "multi" && (
                    <input value={newQ.optionsText} onChange={(e) => setNewQ((p) => ({ ...p, optionsText: e.target.value }))}
                      placeholder="Opzioni separate da virgola (es. Mare, Cantine, Sport)" className={inputCls} data-testid="newq-options" />
                  )}
                </div>
                <button type="button" data-testid="newq-save" disabled={!newQ.it.trim() || savingQ}
                  onClick={async () => {
                    setSavingQ(true);
                    try {
                      const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30);
                      const options = newQ.kind === "multi"
                        ? newQ.optionsText.split(",").map((s) => s.trim()).filter(Boolean).map((label, i) => ({ id: slugify(label) || `opt-${i}`, label: { it: label, en: "", de: "", fr: "" } }))
                        : [];
                      await api.post("/admin/survey/questions", {
                        question: { it: newQ.it.trim(), en: newQ.en.trim(), de: newQ.de.trim(), fr: newQ.fr.trim() },
                        kind: newQ.kind, options, active: true,
                      });
                      toast.success("Domanda aggiunta");
                      setNewQ({ it: "", en: "", de: "", fr: "", kind: "rating", optionsText: "" });
                      loadSurvey();
                    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingQ(false); }
                  }}
                  className="rounded-full bg-terracotta hover:bg-[#d15f2d] disabled:opacity-50 text-white text-sm font-bold px-5 py-2.5 transition-colors self-start">
                  {savingQ ? "Salvo…" : "Aggiungi domanda"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing === "new" ? `Nuova scheda · ${TYPE_LABELS[tab]}` : `Modifica · ${editing?.title?.it || ""}`}</DialogTitle>
          </DialogHeader>
          {editing && (
            <ItemForm
              type={tab}
              item={editing === "new" ? null : editing}
              onClose={() => setEditing(null)}
              onSaved={() => { setEditing(null); loadItems(tab); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
